<template>
    <div class="w-full mb-8 rounded-lg shadow-xs">
        <div class="w-full">
            <!-- A new table for each Survey -->
            <table class="w-full whitespace-no-wrap"
                   :class="{'blur-sm': loading}"
                   v-for="(survey, surveyIndex) in templateSurveyScores"
                   :key="'survey-' + surveyIndex"
            >
                <thead class="cursor-pointer">
                <tr @click="showSurveySectionScores[surveyIndex] = !showSurveySectionScores[surveyIndex]"
                    class="text-md font-bold tracking-wide text-center text-gray-700 border-b dark:border-gray-700 bg-gray-300 dark:text-gray-200 dark:bg-gray-700">
                    <th class="bg-gray-300 px-4 py-3 w-3/5 text-left">
                        <h4 class="text-xl font-bold whitespace-normal">{{ survey.name }}</h4>
                    </th>
                    <th class="bg-gray-300 px-4 py-3">
                        <div class="flex items-center w-full justify-start">
                            <p class="text-xl">Average Score: {{ survey.scores.avgScore }}</p>
                            <span class="inline-block ml-4 px-2 py-1 font-semibold leading-tight text-lg rounded-full"
                                  :style="{
                                        backgroundColor: survey.scores.scoreRatingDetails.bg_color,
                                        color: survey.scores.scoreRatingDetails.text_color,
                                    }"
                            >
                                {{ survey.scores.scoreRatingDetails.label }}
                            </span>
                        </div>
                    </th>
                    <th class="bg-gray-300 px-4 py-3">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </th>
                </tr>
                </thead>
                <tbody v-if="showSurveySectionScores[surveyIndex]"
                       class="bg-white divide-y dark:divide-gray-700 dark:bg-gray-900 text-center">
                    <template v-for="(section, sectionIndex) in survey.sections">
                        <tr v-if="survey.survey_type_id !== 1"
                            :key="'section-' + sectionIndex"
                            @click="showSectionQuestionScores[surveyIndex + '-' + sectionIndex] = !showSectionQuestionScores[surveyIndex + '-' + sectionIndex]"
                            class="cursor-pointer bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-400">
                            <td class="pl-8 pr-4 py-3 w-3/5 text-sm text-left whitespace-normal">
                                <h4 class="text-lg font-semibold">{{ section.name }}</h4>
                            </td>
                            <td class="px-4 py-3">
                                <div class="flex items-center w-full justify-left text-md font-bold">
                                    <p>Average Score: {{ survey.scores.sections[section.id].avgScore }}</p>
                                    <span class="inline-block ml-3 px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:text-white dark:bg-green-600"
                                          :style="{
                                                backgroundColor: survey.scores.sections[section.id].scoreRatingDetails.bg_color,
                                                color: survey.scores.sections[section.id].scoreRatingDetails.text_color,
                                            }"
                                    >
                                        {{ survey.scores.sections[section.id].scoreRatingDetails.label }}
                                    </span>
                                </div>
                            </td>
                            <td class="pl-4 pr-8 py-3">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </td>
                        </tr>
                        <template v-if="showSectionQuestionScores[surveyIndex + '-' + sectionIndex] || survey.survey_type_id === 1">
                            <tr v-for="(question, questionIndex) in section.questions" :key="'question-' + questionIndex"
                                class="text-gray-700 dark:text-gray-400 border-l border-r dark:border-gray-700">
                                <td class="pl-12 pr-4 py-3 w-1/2 text-sm text-left whitespace-normal">
                                    <h4 class="text-lg font-semibold">{{ question.content }}</h4>
                                    <template v-if="question.allow_response_comment">
                                        <button @click="showQuestionComments[surveyIndex + '-' + sectionIndex + '-' + questionIndex] = !showQuestionComments[surveyIndex + '-' + sectionIndex + '-' + questionIndex]"
                                                class="mt-3 mb-2 p-4 text-md font-semibold flex items-center justify-between w-full rounded-md bg-gray-200 dark:bg-gray-600 focus:outline-none">
                                            Comments
                                            <span class="mr-auto ml-3 px-2 py-1 font-semibold leading-tight text-xs text-gray-700 bg-gray-300 rounded-full dark:text-gray-100 dark:bg-gray-700">
                                                {{ survey.scores.sections[section.id].questions[question.id].commentCount }}
                                            </span>
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        <div v-if="showQuestionComments[surveyIndex + '-' + sectionIndex + '-' + questionIndex]" class="w-full py-4 px-6 bg-gray-200 dark:bg-gray-600 rounded-md">
                                            <p v-if="question.response_comment_label" class="mb-2 font-bold">{{ question.response_comment_label }}</p>
                                            <ul class="text-sm list-disc pl-4">
                                                <template v-for="(response,responseIndex) in question.survey_responses">
                                                    <li v-if="response.comment" :key="'comment-' + responseIndex">
                                                        {{ response.comment }}
                                                    </li>
                                                </template>
                                            </ul>
                                        </div>
                                    </template>
                                </td>
                                <td class="px-4 py-3">
                                    <div class="flex items-center w-full justify-left text-sm">
                                        <p>Average Score: {{ survey.scores.sections[section.id].questions[question.id].avgScore }}</p>
                                        <span class="inline-block ml-3 px-2 py-1 font-semibold leading-tight text-red-700 bg-red-100 rounded-full dark:text-white dark:bg-red-600"
                                              :style="{
                                                backgroundColor: survey.scores.sections[section.id].questions[question.id].scoreRatingDetails.bg_color,
                                                color: survey.scores.sections[section.id].questions[question.id].scoreRatingDetails.text_color,
                                            }"
                                        >
                                            {{ survey.scores.sections[section.id].questions[question.id].scoreRatingDetails.label }}
                                        </span>
                                    </div>
                                </td>
                                <td class="pl-4 pr-12 py-3"></td>
                            </tr>
                        </template>
                    </template>
                </tbody>
            </table>
            <div class="p-3 bg-gray-50">
                <table-pagination v-show="paginatedTemplateScores?.data.length"
                                  :preserve-scroll="true"
                                  :pagination-object="paginatedTemplateScores"
                                  @start-visit="$emit('start-visit')"
                                  @finish-visit="$emit('finish-visit')"
                ></table-pagination>
            </div>
        </div>
    </div>
</template>

<script>

import {computed, reactive} from "vue";
import TablePagination from "@/Jetstream/TablePagination";

export default {
    name: "TemplateSurveyScoresExplorer",
    components: {
        TablePagination,
    },
    props: {
        paginatedTemplateScores: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false,
        }
    },
    setup(props) {
        const templateSurveyScores = computed(() => {
            return props.paginatedTemplateScores.data;
        })
        const showSurveySectionScores = reactive({});
        const showSectionQuestionScores = reactive({});
        const showQuestionComments = reactive({});

        return {
            templateSurveyScores,
            showSurveySectionScores,
            showSectionQuestionScores,
            showQuestionComments,
        }
    }
}
</script>

<style scoped>

</style>
